import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import tokens from '../../../assets/theme/tokens';
import { PaymentRequest, PaymentRequestsSummary } from '../types/finance.types';

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

/** What is still owed on one request: nothing once it is Paid or Void. */
export const rowBalance = (row: PaymentRequest): number =>
  row.status === 'Paid' || row.status === 'Void'
    ? 0
    : Number(row.totalAllowed || 0) + Number(row.totalCostContainmentFees || 0) - Number(row.totalPaid || 0);

// Colours checked with the dataviz palette validator (all checks pass; the
// amber/green pair is close for protan readers, so every bar also carries its
// name, sign and amount). Running totals are indigo; the steps between them
// each have their own hue.
const FLOW = {
  total: '#6366f1', // indigo-500 -- running totals
  saving: '#16a34a', // green-600  -- taken off the bill
  fee: '#d97706', // amber-600  -- added on top
  paid: '#0891b2', // cyan-600   -- already paid
  left: '#4338ca', // indigo-700 -- still owed, the headline
};

type Step = {
  label: string;
  sign: '' | '−' | '+' | '=';
  value: number;
  from: number; // where the bar starts, in dollars
  to: number; // where it ends
  color: string;
  strong?: boolean;
};

const useStyles = makeStyles((theme) => ({
  // Same frame as the table card below it, so the page keeps one width.
  card: {
    background: tokens.colors.surface,
    border: `1px solid ${tokens.colors.border}`,
    borderRadius: tokens.radius.lg,
    boxShadow: tokens.shadows.sm,
    padding: theme.spacing(2, 2.5),
    marginBottom: theme.spacing(2),
    display: 'grid',
    gridTemplateColumns: 'minmax(200px, 240px) 1fr',
    gap: theme.spacing(3),
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
  },
  figures: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    paddingRight: theme.spacing(2),
    borderRight: `1px solid ${tokens.colors.border}`,
    [theme.breakpoints.down('sm')]: { borderRight: 'none', paddingRight: 0 },
  },
  eyebrow: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '.06em',
    color: tokens.colors.subtle,
  },
  figure: { fontSize: '1.15rem', fontWeight: 600, color: tokens.colors.text, lineHeight: 1.2 },
  headline: { fontSize: '1.6rem', fontWeight: 700, color: FLOW.left, lineHeight: 1.1 },
  note: { fontSize: '0.72rem', color: tokens.colors.muted },
  flow: { display: 'grid', rowGap: 6 },
  row: {
    display: 'grid',
    gridTemplateColumns: '130px 1fr 110px',
    alignItems: 'center',
    columnGap: theme.spacing(1.5),
    fontSize: '0.8rem',
  },
  label: { color: tokens.colors.subtle, whiteSpace: 'nowrap' },
  strongLabel: { color: tokens.colors.text, fontWeight: 600 },
  track: { position: 'relative', height: 14 },
  bar: {
    position: 'absolute',
    top: 0,
    height: '100%',
    borderRadius: 4,
    minWidth: 2,
    transition: 'left .3s ease, width .3s ease',
  },
  value: { textAlign: 'right', color: tokens.colors.text, fontVariantNumeric: 'tabular-nums' },
}));

type Props = {
  summary?: PaymentRequestsSummary;
};

/**
 * Where the money goes, over every payment request matching the page's
 * filters (all pages, from the API): the bill, less savings, is the allowed
 * amount; plus the fee, the total payment; less what is paid, what is left.
 * Void requests are left out so every step adds up.
 */
export function PaymentsSummaryCards({ summary }: Props) {
  const classes = useStyles();
  const { t } = useTranslation();

  // Every figure coerced to a number, and the derived ones worked out here if
  // the API did not send them, so one missing field cannot collapse the chart.
  const n = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const totalAllowed = n(summary?.totalAllowed);
  const totalFees = n(summary?.totalFees);
  const totalPaid = n(summary?.totalPaid);
  const totalPayment = summary?.totalPayment != null ? n(summary.totalPayment) : totalAllowed + totalFees;
  const s = {
    totalBilled: n(summary?.totalBilled),
    totalSaving: n(summary?.totalSaving),
    totalAllowed,
    totalFees,
    totalPayment,
    totalPaid,
    balance: summary?.balance != null ? n(summary.balance) : totalPayment - totalPaid,
    voidCount: n(summary?.voidCount),
  };
  const fmt = (v: number) => (summary ? money.format(v) : '—');

  const steps: Step[] = [
    { label: 'Bill amount', sign: '', value: s.totalBilled, from: 0, to: s.totalBilled, color: FLOW.total },
    { label: 'Savings', sign: '−', value: s.totalSaving, from: s.totalAllowed, to: s.totalBilled, color: FLOW.saving },
    { label: 'Allowed', sign: '=', value: s.totalAllowed, from: 0, to: s.totalAllowed, color: FLOW.total },
    { label: 'Fee', sign: '+', value: s.totalFees, from: s.totalAllowed, to: s.totalPayment, color: FLOW.fee },
    { label: 'Total payment', sign: '=', value: s.totalPayment, from: 0, to: s.totalPayment, color: FLOW.total },
    { label: 'Paid', sign: '−', value: s.totalPaid, from: s.balance, to: s.totalPayment, color: FLOW.paid },
    { label: 'Left to pay', sign: '=', value: s.balance, from: 0, to: s.balance, color: FLOW.left, strong: true },
  ];

  // One scale for every bar: the longest of the bill and the payment.
  const scale = Math.max(s.totalBilled, s.totalPayment, 1);
  const pct = (v: number) => `${Math.max(0, Math.min(100, (v / scale) * 100))}%`;
  const ofBill = (v: number) =>
    s.totalBilled > 0 ? ` · ${Math.round((v / s.totalBilled) * 100)}% of bill amount` : '';

  return (
    <Box className={classes.card} role="group" aria-label={t('Payment totals')}>
      <Box className={classes.figures}>
        <Box>
          <Typography className={classes.eyebrow}>{t('Left to pay')}</Typography>
          <Typography className={classes.headline} aria-label={t('Left to pay')}>{fmt(s.balance)}</Typography>
        </Box>
        <Box display="flex" style={{ gap: 24 }}>
          <Box>
            <Typography className={classes.eyebrow}>{t('Total payment')}</Typography>
            <Typography className={classes.figure} aria-label={t('Total payment')}>{fmt(s.totalPayment)}</Typography>
          </Box>
          <Box>
            <Typography className={classes.eyebrow}>{t('Paid')}</Typography>
            <Typography className={classes.figure} aria-label={t('Paid')}>{fmt(s.totalPaid)}</Typography>
          </Box>
        </Box>
        {s.voidCount > 0 && (
          <Typography className={classes.note}>
            {t('{{count}} void payments excluded', { count: s.voidCount })}
          </Typography>
        )}
      </Box>

      <Box className={classes.flow} role="list" aria-label={t('Payment flow')}>
        {steps.map((step) => {
          const name = `${step.sign ? `${step.sign} ` : ''}${t(step.label)}`;
          return (
            <Box
              key={step.label}
              className={classes.row}
              role="listitem"
              // Native hover tooltip: the step, its amount and its share of the bill.
              title={`${t(step.label)}: ${fmt(step.value)}${summary ? ofBill(step.value) : ''}`}
            >
              <span className={`${classes.label} ${step.strong ? classes.strongLabel : ''}`}>{name}</span>
              <span className={classes.track} aria-hidden="true">
                <span
                  className={classes.bar}
                  style={{
                    left: pct(Math.min(step.from, step.to)),
                    width: `calc(${pct(Math.abs(step.to - step.from))})`,
                    background: step.color,
                  }}
                />
              </span>
              <span
                className={`${classes.value} ${step.strong ? classes.strongLabel : ''}`}
                aria-label={t(step.label)}
              >
                {step.sign === '−' && summary ? '−' : ''}
                {fmt(step.value)}
              </span>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default PaymentsSummaryCards;
